'use client';

import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import React, { useEffect, useRef, useState } from 'react';
import { urlParameterCodes } from '@/constants/urlParams';
import { getUrlParamValue } from '@/ui/utils/client-url-helpers';
import { useCurrencyFormatter } from '@/ui/hooks/useCurrencyFormatter';
import { useCartStore } from '@/ui/store/cart';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { AnimatePresence, motion } from 'framer-motion';
import {
  useExternalAreaLink,
  useExternalAreaLinkDismissed,
} from '@/ui/providers/externalAreaLinkProvider';
import ShippingAvailabilityModal from '@/ui/components/shared/ShippingAvailabilityModal';
import { ArrowLongLeftIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'next-intl';
import TilesArrayRenderer from '@/ui/components/tileRenderers/PageGridRenderer/tiles-array-renderer';
import { ProductImageGallery } from '@/ui/components/product-image-gallery/product-image-gallery';
import { ProductBadgeOverlay } from '@/ui/components/product-badge/product-badge-overlay';
import {
  ImageFragment,
  SubscriptionPlansFragment,
  TileFragmentFragment,
  VideoFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { BaseProduct } from '@/ui/services/BaseProductService';
import { Smartlock } from '@/ui/services/SmartlockService';
import { Accessory } from '@/ui/services/AccessoryService';
import { Bundle } from '@/ui/services/BundleService';
import { DesktopAddToCart } from '@/ui/components/pages/ProductDetail/add-to-cart-bottom/desktop-add-to-cart/desktop-add-to-cart';
import { MobileAddToCart } from '@/ui/components/pages/ProductDetail/add-to-cart-bottom/mobile-add-to-cart/mobile-add-to-cart';
import { ClubOnlyPurchaseActions } from '@/ui/components/club-only-purchase-actions/club-only-purchase-actions';
import { useClubOnlyPurchase } from '@/ui/components/club-only-purchase-actions/use-club-only-purchase';
import { useProductRuleService } from '@/ui/providers/product-rules-provider';
import { CLUB_CHOOSER_NAG_VALUE } from '@/utils/constants';
import { ParsedShopFeatures } from '../SmartlockDetail/types/ParsedShopFeatures';
import { ParsedProductVariant } from '../types/ParsedProductVariant';
import { SelectedProductVariants } from '../types/SelectedProductVariants';
import { ParsedSmartlockVariant } from '../SmartlockDetail/types/ParsedSmartlockVariant';
import { ParsedAccessoryVariant } from '../AccessoryDetail/types/ParsedAccessoryVariant';
import { isChooserSelectionRestricted } from './chooser-selection-restriction';

type ProductChooserWrapperProps = {
  product: BaseProduct | Smartlock | Accessory;
  shopFeatures?: ParsedShopFeatures;
  currentVariant: ParsedProductVariant | ParsedSmartlockVariant | ParsedAccessoryVariant;
  subscriptionPlan?: SubscriptionPlansFragment;
  addToCart: () => void;
  children: React.ReactNode;
  selectedBundle?: Bundle;
  selectedBundleProductVariants?: SelectedProductVariants;
  galleryImages?: ImageFragment[];
  videos?: VideoFragment[];
  additionalInformationTiles?: TileFragmentFragment[];
  onAdditionalInformationClose?: () => void;
};

export default function ProductChooserWrapper({
  product,
  shopFeatures,
  currentVariant,
  subscriptionPlan,
  addToCart,
  children,
  selectedBundle,
  selectedBundleProductVariants,
  galleryImages,
  videos,
  additionalInformationTiles,
  onAdditionalInformationClose,
}: Readonly<ProductChooserWrapperProps>) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { formatCurrency } = useCurrencyFormatter();
  const { setShowNotifyMe } = useCartStore();
  const { country } = useLocalization();
  const notifyOosTriggeredRef = useRef(false);

  // When the URL contains ?notifyOos=true (set by /direct-purchase generic
  // mode on OOS items), open NotifyMeModal once after variant/bundle settle.
  useEffect(() => {
    if (notifyOosTriggeredRef.current) {
      return;
    }
    const flag = getUrlParamValue(urlParameterCodes.productDetail.notifyOutOfStock);
    if (flag !== 'true') {
      return;
    }
    if (!currentVariant && !selectedBundle) {
      return;
    }
    notifyOosTriggeredRef.current = true;
    setShowNotifyMe(true, currentVariant ?? null, selectedBundle ?? null);
  }, [currentVariant, selectedBundle, setShowNotifyMe]);
  const externalAreaLink = useExternalAreaLink();
  const { isDismissed, dismiss } = useExternalAreaLinkDismissed();

  const priceDisplay = () => {
    if (subscriptionPlan?.periods[0]?.initial) {
      return formatCurrency(subscriptionPlan.periods[0].initial.price);
    }

    if (selectedBundle) {
      return formatCurrency(selectedBundle.getFinalPrice(selectedBundleProductVariants));
    }

    return currentVariant.priceVariant && formatCurrency(currentVariant.priceVariant);
  };

  const isVariantAvailable = () => {
    if (selectedBundle) {
      return selectedBundle.isBundleVariantInStock(selectedBundleProductVariants);
    }
    return product.isVariantInStock(currentVariant);
  };

  const onNotify = () => {
    setShowNotifyMe(true, currentVariant, selectedBundle || null);
  };

  const t = useTranslations('FullScreenOverlay');

  const effectiveExternalAreaLink = isDismissed ? null : externalAreaLink;
  const handleAddToCart = effectiveExternalAreaLink ? () => setIsModalOpen(true) : addToCart;

  const isSelectionRegionRestricted = isChooserSelectionRestricted({
    currentVariant,
    selectedBundle,
    selectedBundleProductVariants,
    country,
  });

  const showAsAvailable =
    (isVariantAvailable() || !!externalAreaLink) && !isSelectionRegionRestricted;

  const productRuleService = useProductRuleService();
  const variantReference = product.getVariantReference(currentVariant);
  const bundleConfiguration = selectedBundle?.getConfiguration(selectedBundleProductVariants);
  const productBadges = bundleConfiguration
    ? productRuleService.getBundleBadges(bundleConfiguration)
    : productRuleService.getVariantBadges(variantReference);

  const isSelectionRestrictedToNukiClub = bundleConfiguration
    ? productRuleService.isBundleRestrictedToNukiClub(bundleConfiguration)
    : productRuleService.isVariantRestrictedToNukiClub(variantReference);
  const clubOnlyPurchase = useClubOnlyPurchase(
    isSelectionRestrictedToNukiClub,
  );
  const isWaitingForClubCheck = clubOnlyPurchase.gate === 'waiting';
  const clubOnlyPurchaseActions =
    clubOnlyPurchase.gate === 'club-actions' ? (
      <ClubOnlyPurchaseActions
        isLoggedIn={clubOnlyPurchase.isLoggedIn}
        nag={CLUB_CHOOSER_NAG_VALUE}
        onLogIn={clubOnlyPurchase.logInToUnlock}
      />
    ) : undefined;

  return (
    <div className="grid-cols-12 max-lg:flex max-lg:h-full max-lg:flex-col lg:grid">
      {/* Additional information tiles */}
      <AnimatePresence>
        {!!additionalInformationTiles && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
            className="fixed inset-y-0 z-10 h-full w-full overflow-auto bg-white lg:w-7/12 xl:w-2/3"
          >
            <div className="m-3 flex items-start justify-between sm:m-4">
              <div className="flex items-center">
                <button
                  type="button"
                  className="flex items-center gap-2 font-medium"
                  onClick={() => {
                    onAdditionalInformationClose?.();
                  }}
                  data-tile-element-id="product-chooser-additional-info-back"
                >
                  <ArrowLongLeftIcon className="h-8 w-8 text-primary-100" />
                  {t('Back')}
                </button>
              </div>
            </div>
            <TilesArrayRenderer tiles={additionalInformationTiles} />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative hidden flex-col justify-center bg-neutral-5 md:flex md:h-screen md:supports-[height:100svh]:h-[100svh] lg:col-span-7 xl:col-span-8">
        {/* The badges sit in the column's top corners, just below the overlay's Back header. */}
        <ProductBadgeOverlay
          badges={productBadges}
          className="inset-x-4 top-16 sm:inset-x-6"
          badgeSize="large"
        />
        {/* Image gallery */}
        <ProductImageGallery galleryImages={galleryImages} videos={videos} />
        {/* Shop features */}
        <div className="my-10 flex w-full justify-center gap-16">
          {shopFeatures?.map((feature) => (
            <div key={feature?.text.text ?? feature?.icon.images?.[0]?.key}>
              {feature?.icon && (
                <OptimizedImage
                  imageComponent={feature.icon}
                  sizes="100px"
                  width={100}
                  height={100}
                  className="mx-auto mb-3 h-10 w-auto sm:mb-5"
                />
              )}
              {feature?.text.text && <p className="text-center font-bold">{feature.text.text}</p>}
            </div>
          ))}
        </div>
      </div>

      <div className="z-10 flex flex-col bg-white px-4 shadow-xl max-lg:flex-1 sm:px-6 lg:col-span-5 lg:h-screen lg:overflow-y-auto lg:px-0 lg:supports-[height:100svh]:h-[100svh] xl:col-span-4">
        {/* Right side of the chooser */}
        <div className="flex-grow space-y-10 pb-37 md:max-lg:pt-8 lg:p-6 lg:pb-6">{children}</div>
        <MobileAddToCart
          addToCart={handleAddToCart}
          onNotify={onNotify}
          price={priceDisplay()}
          productName={selectedBundle?.name ?? product.name}
          isAvailable={showAsAvailable}
          isBundleSelected={!!selectedBundle}
          isUS={country.toUpperCase() === 'US'}
          disabled={isWaitingForClubCheck}
          purchaseActions={clubOnlyPurchaseActions}
        />
        <DesktopAddToCart
          addToCart={handleAddToCart}
          onNotify={onNotify}
          price={priceDisplay()}
          isAvailable={showAsAvailable}
          isBundleSelected={!!selectedBundle}
          isUS={country.toUpperCase() === 'US'}
          disabled={isWaitingForClubCheck}
          purchaseActions={clubOnlyPurchaseActions}
        />
        {externalAreaLink && (
          <ShippingAvailabilityModal
            open={isModalOpen && !isDismissed}
            onClose={() => setIsModalOpen(false)}
            onTryAnyway={() => {
              setIsModalOpen(false);
              dismiss();
            }}
            externalLink={externalAreaLink}
          />
        )}
      </div>
    </div>
  );
}
